// SCR-STAFF-963 (AP-ENG-02؛ US-ENG-021؛ BR-ENG-021): قواعد پیگیری خودکار — هشت قاعده پیش‌فرض (برآورد بی‌پاسخ، انقضای برآورد، پس از درمان، عقب‌ماندن طرح،
// انقضای پکیج، غیبت یا لغو، عدم مراجعه، فراخوان) با فعال/غیرفعال، فاصله، اولویت، نوع، مسئول ثابت و هدف. نیاز به رضایت تبلیغاتی ثابت و فقط برای قواعد «بازگشت»
// است (پس از درمان و فراخوان ایمنی هرگز — C-AGENT-1563). هر محرک برای هر موضوع حداکثر یک وظیفه باز می‌سازد (AC-ENG-021.2).
import EditIcon from '@mui/icons-material/EditOutlined';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type EngAssigneeOption,
  type EngFollowUpRuleView,
  type EngOfferingOption,
} from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  ErrorState,
  faDigits,
  FormDialog,
  latinDigits,
  LoadingState,
  PageHeader,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  zRequiredText,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { AssigneePicker, engKeys, FOLLOWUP_TYPES, OfferingPicker, PRIORITIES, priorityTone } from './common';

export const SCR_STAFF_963 = 'SCR-STAFF-963';
export const FOLLOWUP_RULES_PATH = '/crm/followup-rules';

const rulesKey = (tenantId: string | undefined) => [...engKeys.all(tenantId), 'followup-rules'] as const;

/** بازه مجاز فاصله: «عدم مراجعه» آستانه ۳۰ تا ۳۶۵ روز؛ بقیه ۰ تا ۶۰ روز. */
export function delayRange(trigger: EngFollowUpRuleView['trigger']): [number, number] {
  return trigger === 'NoRecentVisit' ? [30, 365] : [0, 60];
}

export function FollowUpRulesPage() {
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const write = useWriteAccess('eng.followup.view_all');
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<EngFollowUpRuleView | null>(null);
  const rules = useQuery({
    queryKey: rulesKey(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/engagement/followup-rules')),
  });

  const toggle = useMutation({
    mutationFn: (rule: EngFollowUpRuleView) =>
      unwrap(
        tenantApi.PUT('/api/v1/engagement/followup-rules/{trigger}', {
          params: { path: { trigger: rule.trigger } },
          body: {
            enabled: !rule.enabled,
            delayDays: null,
            priority: null,
            taskType: null,
            assigneeMembershipId: rule.assigneeMembershipId ?? null,
            goal: null,
            categoryIds: null,
            rowVersion: Number(rule.rowVersion) || null,
          },
        }),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: rulesKey(session?.tenantId) }),
  });

  return (
    <section data-screen-id={SCR_STAFF_963}>
      <PageHeader titleKey="eng:rules.title" subtitleKey="eng:rules.subtitle" />
      <Stack spacing={2}>
        <WriteNotice write={write} />
        <Alert severity="info">{t('eng:rules.dedup_hint')}</Alert>
        {toggle.error && <ErrorState error={toggle.error} compact />}
        {rules.isPending && <LoadingState rows={8} />}
        {rules.error && <ErrorState error={rules.error} onRetry={() => void rules.refetch()} />}
        {rules.data && (
          <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label={t('eng:rules.title')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('eng:rules.columns.trigger')}</TableCell>
                  <TableCell>{t('eng:rules.columns.enabled')}</TableCell>
                  <TableCell>{t('eng:rules.columns.delay')}</TableCell>
                  <TableCell>{t('eng:rules.columns.priority')}</TableCell>
                  <TableCell>{t('eng:rules.columns.assignee')}</TableCell>
                  <TableCell>{t('eng:rules.columns.goal')}</TableCell>
                  <TableCell>{t('eng:rules.columns.open_tasks')}</TableCell>
                  <TableCell>{t('eng:rules.columns.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rules.data.map((rule) => (
                  <TableRow key={rule.trigger} data-testid={`rule-${rule.trigger}`}>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" fontWeight={600}>
                          {t(`eng:trigger.${rule.trigger}`)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t(`eng:trigger_hint.${rule.trigger}`)}
                        </Typography>
                        {rule.requiresMarketingConsent ? (
                          <Chip
                            size="small"
                            variant="outlined"
                            color="warning"
                            label={t('eng:rules.consent_required')}
                            data-testid={`rule-consent-${rule.trigger}`}
                            sx={{ alignSelf: 'flex-start' }}
                          />
                        ) : (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('eng:rules.consent_free')}
                            sx={{ alignSelf: 'flex-start' }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={rule.enabled}
                        disabled={!write.canWrite || toggle.isPending}
                        onChange={() => toggle.mutate(rule)}
                        slotProps={{
                          input: {
                            'aria-label': t('eng:rules.toggle', { name: t(`eng:trigger.${rule.trigger}`) }),
                            'data-testid': `rule-toggle-${rule.trigger}`,
                          } as never,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {t(
                        rule.trigger === 'NoRecentVisit'
                          ? 'eng:rules.threshold_days'
                          : 'eng:rules.delay_days',
                        { count: Number(rule.delayDays), days: faDigits(Number(rule.delayDays)) },
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        tone={priorityTone(rule.priority)}
                        labelKey={`eng:priority.${rule.priority}`}
                      />
                    </TableCell>
                    <TableCell>{rule.assigneeName ?? t('eng:rules.auto_assignee')}</TableCell>
                    <TableCell>
                      {rule.goal}
                      {rule.categories.length > 0 && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {t('eng:rules.categories')}:{' '}
                          {rule.categories.map((category) => category.name ?? '—').join(t('common:list_separator'))}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{faDigits(Number(rule.openTasks))}</TableCell>
                    <TableCell>
                      <Tooltip title={t('eng:rules.edit')}>
                        <span>
                          <IconButton
                            aria-label={t('eng:rules.edit')}
                            disabled={!write.canWrite}
                            onClick={() => setEditing(rule)}
                            data-testid={`rule-edit-${rule.trigger}`}
                          >
                            <EditIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Stack>
      {editing && <RuleDialog rule={editing} onClose={() => setEditing(null)} />}
    </section>
  );
}

const ruleSchema = z.object({
  enabled: z.boolean(),
  delayDays: z.string().regex(/^\d{1,3}$/, { error: 'eng:validation.delay_range' }),
  priority: z.enum(['High', 'Normal', 'Low']),
  taskType: z.enum(['Call', 'Message', 'InternalReminder']),
  goal: zRequiredText(300),
});
type RuleForm = z.input<typeof ruleSchema>;

function RuleDialog({ rule, onClose }: { rule: EngFollowUpRuleView; onClose: () => void }) {
  const { t } = useTranslation('eng');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [min, max] = delayRange(rule.trigger);
  const [assignee, setAssignee] = useState<EngAssigneeOption | null>(
    rule.assigneeMembershipId
      ? { membershipId: rule.assigneeMembershipId, name: rule.assigneeName ?? '', roles: [] }
      : null,
  );
  const [categories, setCategories] = useState<EngOfferingOption[]>(
    rule.categories.map((category) => ({
      id: category.id,
      name: category.name ?? '',
      kind: 'Category',
      price: null,
    })),
  );
  const [picker, setPicker] = useState<EngOfferingOption | null>(null);
  const [extraDirty, setExtraDirty] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();
  const { control, handleSubmit, formState, setError } = useForm<RuleForm>({
    resolver: zodResolver(ruleSchema),
    defaultValues: {
      enabled: rule.enabled,
      delayDays: String(rule.delayDays),
      priority: rule.priority as RuleForm['priority'],
      taskType: rule.taskType as RuleForm['taskType'],
      goal: rule.goal,
    },
  });
  const save = useMutation({
    mutationFn: (form: RuleForm) =>
      unwrap(
        tenantApi.PUT('/api/v1/engagement/followup-rules/{trigger}', {
          params: { path: { trigger: rule.trigger } },
          body: {
            enabled: form.enabled,
            delayDays: Number(latinDigits(form.delayDays)),
            priority: form.priority,
            taskType: form.taskType,
            assigneeMembershipId: assignee?.membershipId ?? null,
            goal: form.goal,
            categoryIds: rule.trigger === 'PostTreatment' ? categories.map((category) => category.id) : null,
            rowVersion: Number(rule.rowVersion) || null,
          },
        }),
      ),
    onMutate: () => setSubmitError(undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: rulesKey(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, setError, {
        fields: ['goal', 'delayDays', 'priority', 'taskType'],
      });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  const submit = (form: RuleForm) => {
    const days = Number(latinDigits(form.delayDays));
    if (days < min || days > max) {
      setError('delayDays', { message: 'eng:validation.delay_range' });
      return;
    }
    save.mutate(form);
  };

  return (
    <FormDialog
      open
      maxWidth="sm"
      screenId="SCR-STAFF-963-EDIT"
      titleKey="eng:rules.edit_title"
      onSubmit={() => void handleSubmit(submit)()}
      onClose={onClose}
      isDirty={formState.isDirty || extraDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <Typography variant="subtitle1" data-testid="rule-dialog-trigger">
          {t(`eng:trigger.${rule.trigger}`)}
        </Typography>
        <Controller
          control={control}
          name="enabled"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch checked={field.value} onChange={(event) => field.onChange(event.target.checked)} />
              }
              label={t('eng:rules.columns.enabled')}
            />
          )}
        />
        <TextFieldRHF
          control={control}
          name="delayDays"
          labelKey={rule.trigger === 'NoRecentVisit' ? 'eng:rules.threshold_label' : 'eng:rules.delay_label'}
          helperKey={rule.trigger === 'NoRecentVisit' ? 'eng:rules.range_threshold' : 'eng:rules.range_delay'}
          inputProps={{ inputMode: 'numeric', dir: 'ltr', 'data-testid': 'rule-delay' }}
          required
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <SelectRHF
            control={control}
            name="priority"
            labelKey="eng:followup_form.priority"
            fullWidth
            options={PRIORITIES.map((value) => ({ value, labelKey: `eng:priority.${value}` }))}
          />
          <SelectRHF
            control={control}
            name="taskType"
            labelKey="eng:followup_form.type"
            fullWidth
            options={FOLLOWUP_TYPES.map((value) => ({ value, labelKey: `eng:followup_type.${value}` }))}
          />
        </Stack>
        <TextFieldRHF
          control={control}
          name="goal"
          labelKey="eng:followup_form.goal"
          helperKey="eng:common.no_clinical_hint"
          required
          fullWidth
          inputProps={{ 'data-testid': 'rule-goal' }}
        />
        <AssigneePicker
          value={assignee}
          onChange={(next) => {
            setAssignee(next);
            setExtraDirty(true);
          }}
          labelKey="eng:rules.fixed_assignee"
          testId="rule-assignee"
        />
        <Typography variant="caption" color="text.secondary">
          {t(rule.trigger === 'BatchRecall' ? 'eng:rules.recall_assignee_hint' : 'eng:rules.assignee_hint')}
        </Typography>
        {rule.trigger === 'PostTreatment' && (
          <Stack spacing={1} data-testid="rule-categories">
            <Typography variant="subtitle2">{t('eng:rules.categories')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('eng:rules.categories_hint')}
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {categories.map((category) => (
                <Chip
                  key={category.id}
                  label={category.name}
                  onDelete={() => {
                    setCategories(categories.filter((item) => item.id !== category.id));
                    setExtraDirty(true);
                  }}
                />
              ))}
            </Stack>
            <OfferingPicker
              categories
              value={picker}
              onChange={(next) => {
                if (next && next.kind === 'Category' && !categories.some((item) => item.id === next.id)) {
                  setCategories([...categories, next]);
                  setExtraDirty(true);
                }
                setPicker(null);
              }}
              labelKey="eng:rules.add_category"
              testId="rule-category-picker"
            />
          </Stack>
        )}
      </Stack>
    </FormDialog>
  );
}
